/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useRef, useCallback, useMemo } from "react";
import LogicContext from "../../Context/LogicContext";
import { SetStockTransferList, GetStockTransferList } from "../../store/stockTransferAtom";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdRefresh } from "react-icons/io";
import { FaPlus } from "react-icons/fa";
import { useForm } from "react-hook-form";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import { stockTransferService } from "../../services/stockTransferService";
import { formatCustomDate, formatCustomDateWithoutTime } from "../../helpers/formatCustomDate";

// Removed unused imports for cleanliness.

const StockTransferMainPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const { showToast } = useContext(LogicContext);

  const setStockTransferData = SetStockTransferList();
  const getStockTransferData = GetStockTransferList();
  const [tableData, setTableData] = useState([]);
  const [itemSelected, setItemSelected] = useState([]);
  const mountRef = useRef(false);
  const [searchText, setSearchText] = useState("");

  const { register, getValues } = useForm({
    defaultValues: {
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0],
    },
  });

  // Memoized columns with corrections: Added Status column, fixed Encoded Date.
  const columns = useMemo(
    () => [
      {
        title: "Ref",
        key: "ref_no",
        dataIndex: "ref_no",
        className: "text-blue-600 font-medium",
        sorter: (a, b) => (a?.ref_no ?? 0) - (b?.ref_no ?? 0),
      },
      {
        title: "Transfer No.",
        dataIndex: "transfer_number",
        sorter: (a, b) => (a?.transfer_number ?? 0) - (b?.transfer_number ?? 0),
      },
      {
        title: "Transfer Date",
        dataIndex: "transfer_date",
        sorter: (a, b) => new Date(a.transfer_date) - new Date(b.transfer_date),
        render: (record) => <span>{formatCustomDateWithoutTime(record)}</span>,
      },
      {
        title: "From",
        dataIndex: "warehouse_from_name",
        sorter: (a, b) => (a?.warehouse_from_name ?? "").localeCompare(b?.warehouse_from_name ?? ""),
      },
      {
        title: "To",
        dataIndex: "warehouse_to_name",
        sorter: (a, b) => (a?.warehouse_to_name ?? "").localeCompare(b?.warehouse_to_name ?? ""),
      },
      {
        title: "Created By",
        dataIndex: "encoded_by_name",
        sorter: (a, b) => (a?.encoded_by_name ?? "").localeCompare(b?.encoded_by_name ?? ""),
      },
      {
        title: "Status",
        dataIndex: "status_name",
        sorter: (a, b) => (a?.status_name ?? "").localeCompare(b?.status_name ?? ""),
      },
      {
        title: "Encoded Date",
        dataIndex: "encoded_date",
        sorter: (a, b) => new Date(a.encoded_date) - new Date(b.encoded_date),
        render: (record) => <span>{formatCustomDateWithoutTime(record)}</span>,
      },
    ],
    []
  );

  // Set table data from global state or fetch
  useEffect(() => {
    if (getStockTransferData?.stock_transfer_info?.length > 0) {
      setTableData(getStockTransferData?.stock_transfer_info);
    } else {
      getStockListFromAPI();
    }
    return () => {
    };
  }, []);

  // Handle session-based reload
  useEffect(() => {
    if (sessionStorage.getItem("reloadPage") === "true") {
      sessionStorage.removeItem("reloadPage");
      getStockListFromAPI();
    }
  }, []);

  // Fetch stock transfer list
  const getStockListFromAPI = useCallback(async () => {
    try {
      const response = await stockTransferService.stockTransferList();
      const serverData = response.data;
      if ( serverData) {
        const stock_transfer_info = serverData?.stock_transfer_info || [];
        const warehouse = serverData?.warehouse || [];
        setStockTransferData({ ...serverData, stock_transfer_info, warehouse });
      setTableData(response.data?.stock_transfer_info || []);

      }
    } catch (err) {
      console.error(err);
    }
  }, [setStockTransferData]);

  const selectedTableItems = useCallback((itemList) => {
    setItemSelected(itemList);
  }, []);

  const addNewStockTransfer = useCallback(() => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  }, [currModuleId, currModuleName, currUserMenu, navigate]);

  const searchByDate = useCallback(async () => {
    const formData = getValues();
    try {
      const response = await stockTransferService.stockTransferByDate(formData);
      if (response?.data?.length) {
        setTableData(response.data);
      } else {
        showToast("No result found", "");
        setTableData([]);
      }
    } catch (err) {
      showToast("Error", "error");
    }
  }, [getValues, showToast]);

  const handleRowClick = useCallback(
    (data) => {
      const id = data["ref_no"];
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
    },
    [currModuleId, currModuleName, currUserMenu, navigate]
  );

  const onClearInput = () => {
    setSearchText("");
    setTableData(getStockTransferData?.stock_transfer_info || []);
  };

  const handleSearch = (text) => {
    setSearchText(text);
    if (text.trim() === "") {
      setTableData(getStockTransferData?.stock_transfer_info || []);
      return;
    }
    const searchTerm = text.toLowerCase();
    const filtered = (getStockTransferData?.stock_transfer_info || []).filter((item) =>
      Object.values(item).some((value) => value?.toString().toLowerCase().includes(searchTerm))
    );
    setTableData(filtered);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 space-y-4 md:space-y-0">
        <h2 className="text-2xl font-bold text-gray-800">Stock Transfer</h2>
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
          {/* Date Search */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end space-y-2 sm:space-y-0 sm:space-x-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                {...register("from_date")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                type="date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                {...register("to_date")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                type="date"
              />
            </div>
            <button
              onClick={searchByDate}
              type="button"
              className="mt-2 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {/* Refresh Button */}
          <button
            onClick={getStockListFromAPI}
            className="p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
             <IoMdRefresh className="h-[20px] w-[20px] text-gray-600" />
          </button>
          {/* Add New Button */}
          <button
            onClick={addNewStockTransfer}
            className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <FaPlus className="h-4 w-4" />
            <span className="text-white-500" >Add New</span>
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search in table..."
          value={searchText}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && searchText === "") {
              onClearInput();
            }
          }}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <GeneralCustomTable
          tableHeadList={columns}
          tableDataList={tableData}
          allowCheckBox={true}
          setSelectedRows={selectedTableItems}
          rowKey={(record) => record.ref_no}
          handleRowClick={handleRowClick}
        />
      </div>
    </div>
  );
};

export default React.memo(StockTransferMainPage);